// frontend/components/sidebar/MoveDocModal.tsx

"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { Search, ChevronRight, FileText, X } from "lucide-react";
import * as Lucide from "lucide-react";
import { getTwemojiSvgUrl } from "../../utils/emoji";

interface MoveDocModalProps {
  visible: boolean;
  docId: string | null;
  documents: any[];
  user: any;
  sourceWorkspaceType?: string;
  x?: number;
  y?: number;
  onClose: () => void;
  onMove: (targetParentId: string | null, targetWorkspaceType: string) => void;
}

function DocIcon({ icon, size = 14 }: { icon: string | null | undefined; size?: number }) {
  if (!icon) return <FileText size={size} className="text-gray-400 shrink-0" />;
  if (icon.startsWith("emoji:")) {
    const code = icon.replace("emoji:", "");
    if (/^[0-9a-f-]+$/.test(code)) {
      return <img src={getTwemojiSvgUrl(code)} alt="" style={{ width: size, height: size }} className="object-contain inline-block shrink-0" />;
    }
    return <span style={{ fontSize: size - 2 }} className="leading-none shrink-0">{code}</span>;
  }
  if (icon.startsWith("lucide:")) {
    const parts = icon.replace("lucide:", "").split(":");
    const Comp = (Lucide as any)[parts[0]];
    if (Comp) return <Comp size={size} color={parts[1] !== "currentColor" ? parts[1] : undefined} className="shrink-0" />;
  }
  if (icon.startsWith("upload:") || icon.startsWith("data:")) {
    const src = icon.startsWith("upload:") ? icon.replace("upload:", "") : icon;
    return <img src={src} alt="" style={{ width: size, height: size }} className="object-cover rounded shrink-0" />;
  }
  return <FileText size={size} className="text-gray-400 shrink-0" />;
}

function DocTreeItem({
  doc, documents, excludeId, depth, onSelect, expandedIds, onToggleExpand
}: {
  doc: any;
  documents: any[];
  excludeId: string | null;
  depth: number;
  onSelect: (id: string, wsType: string) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}) {
  if (doc.id === excludeId || doc.isDeleted) return null;
  const children = documents.filter(d => d.parentId === doc.id && d.id !== excludeId && !d.isDeleted);
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(doc.id);

  return (
    <div>
      <div
        className="flex items-center gap-1 rounded-md hover:bg-gray-100 dark:hover:bg-zinc-700/60 cursor-pointer group"
        style={{ paddingLeft: `${8 + depth * 16}px`, paddingRight: 8, paddingTop: 5, paddingBottom: 5 }}
        onClick={() => onSelect(doc.id, doc.workspaceType)}
      >
        <button
          className={`shrink-0 w-4 h-4 flex items-center justify-center transition-transform ${isExpanded ? 'rotate-90' : ''} ${hasChildren ? 'text-gray-500 dark:text-zinc-300' : 'opacity-0 pointer-events-none'}`}
          onClick={(e) => { e.stopPropagation(); onToggleExpand(doc.id); }}
        >
          <ChevronRight size={13} strokeWidth={2.5} />
        </button>
        <DocIcon icon={doc.icon} size={13} />
        <span className="text-[13px] text-gray-700 dark:text-zinc-200 truncate ml-0.5">{doc.title || "새 페이지"}</span>
      </div>
      {isExpanded && hasChildren && children.map(child => (
        <DocTreeItem key={child.id} doc={child} documents={documents} excludeId={excludeId} depth={depth + 1} onSelect={onSelect} expandedIds={expandedIds} onToggleExpand={onToggleExpand} />
      ))}
    </div>
  );
}

export default function MoveDocModal({ visible, docId, documents, user, sourceWorkspaceType = "PERSONAL", x = 0, y = 0, onClose, onMove }: MoveDocModalProps) {
  const [query, setQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (visible) {
      setQuery("");
      setExpandedIds(new Set());
      // 뷰포트 경계를 고려한 위치 계산
      const modalW = 320;
      const modalH = 400;
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      let left = x;
      let top = y;
      if (left + modalW > winW) left = Math.max(8, winW - modalW - 8);
      if (top + modalH > winH) top = Math.max(8, winH - modalH - 8);
      setPos({ top, left });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [visible, x, y]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 이동 가능한 목적지 필터 기준:
  // - 팀스페이스 문서 이동 시: 유저의 소속 팀이 1순위인 팀스페이스만
  // - 개인페이지 문서 이동 시: 위 팀스페이스 + 개인페이지
  // - 공유 스페이스 문서(내 부서가 1순위가 아닌 팀 문서)는 목적지에서 제외
  const canBeDestination = (doc: any): boolean => {
    if (doc.id === docId || doc.isDeleted) return false;
    if (doc.workspaceType === "TEAM") {
      const primaryDept = doc.accessDept?.split(",")[0]?.trim();
      return primaryDept === user?.department;
    }
    if (doc.workspaceType === "PERSONAL") {
      return sourceWorkspaceType === "PERSONAL";
    }
    return false;
  };

  const teamRoots = useMemo(() =>
    documents.filter(d => d.workspaceType === "TEAM" && !d.parentId && canBeDestination(d)),
    [documents, docId, user?.department, sourceWorkspaceType]);

  const personalRoots = useMemo(() =>
    sourceWorkspaceType === "PERSONAL"
      ? documents.filter(d => d.workspaceType === "PERSONAL" && !d.parentId && canBeDestination(d))
      : [],
    [documents, docId, sourceWorkspaceType]);

  const filteredDocs = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return documents.filter(d => canBeDestination(d) && (d.title || "").toLowerCase().includes(q)).slice(0, 12);
  }, [query, documents, docId, user?.department, sourceWorkspaceType]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-60"
      onClick={onClose}
    >
      <div
        className="absolute bg-white dark:bg-[#2f2f2f] rounded-xl shadow-2xl w-80 max-h-100 flex flex-col border border-gray-200 dark:border-zinc-700/50 overflow-hidden"
        style={{ top: pos.top, left: pos.left }}
        onClick={e => e.stopPropagation()}
      >
        {/* 검색창 */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-zinc-700/60">
          <Search size={14} className="text-gray-400 dark:text-zinc-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="페이지 옮길 곳 선택"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-[13px] text-gray-700 dark:text-zinc-200 placeholder-gray-400 dark:placeholder-zinc-500 outline-none"
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300">
              <X size={13} />
            </button>
          )}
        </div>

        <div className="overflow-y-auto flex-1 py-1.5">
          {query.trim() ? (
            filteredDocs.length > 0 ? (
              filteredDocs.map(doc => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-zinc-700/60 cursor-pointer"
                  onClick={() => onMove(doc.id, doc.workspaceType)}
                >
                  <DocIcon icon={doc.icon} size={13} />
                  <span className="text-[13px] text-gray-700 dark:text-zinc-200 truncate">{doc.title || "새 페이지"}</span>
                </div>
              ))
            ) : (
              <p className="text-[12px] text-gray-400 dark:text-zinc-500 text-center py-6">결과 없음</p>
            )
          ) : (
            <>
              {teamRoots.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[11px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider">팀스페이스</div>
                  {teamRoots.map(doc => (
                    <DocTreeItem
                      key={doc.id}
                      doc={doc}
                      documents={documents}
                      excludeId={docId}
                      depth={0}
                      onSelect={(id, wsType) => onMove(id, wsType)}
                      expandedIds={expandedIds}
                      onToggleExpand={toggleExpand}
                    />
                  ))}
                </>
              )}
              {personalRoots.length > 0 && (
                <>
                  <div className={`px-3 py-1 text-[11px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider ${teamRoots.length > 0 ? "mt-1" : ""}`}>개인 페이지</div>
                  {personalRoots.map(doc => (
                    <DocTreeItem
                      key={doc.id}
                      doc={doc}
                      documents={documents}
                      excludeId={docId}
                      depth={0}
                      onSelect={(id, wsType) => onMove(id, wsType)}
                      expandedIds={expandedIds}
                      onToggleExpand={toggleExpand}
                    />
                  ))}
                </>
              )}
              {teamRoots.length === 0 && personalRoots.length === 0 && (
                <p className="text-[12px] text-gray-400 dark:text-zinc-500 text-center py-6">이동 가능한 위치가 없습니다.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
